import { getApiBaseUrl } from '../lib/getApiUrl';
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AuditLog,
  AuditLogFilters,
  AuditLogsResponse,
  AuditLogPagination
} from '@/types/auditLog';
import { User } from '@/lib/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Fetch audit logs with filters
const fetchAuditLogs = async (filters: AuditLogFilters): Promise<AuditLogsResponse> => {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.entityId) params.append('entityId', filters.entityId);
  if (filters.action) params.append('action', filters.action);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  if (filters.types && filters.types.length > 0) {
    params.append('types', filters.types.join(','));
  }

  const response = await fetch(`${API_BASE_URL}/audit-logs?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch audit logs');
  }

  const data = await response.json();

  // Handle API response structure: {success, data: [...logs], meta: {page, limit, total, totalPages}}
  if (data.success && Array.isArray(data.data)) {
    return {
      logs: data.data,
      pagination: {
        page: data.meta?.page || filters.page || 1,
        limit: data.meta?.limit || filters.limit || 20,
        total: data.meta?.total || data.data.length,
        pages: data.meta?.totalPages || 1,
      },
    };
  }

  // Handle nested structure {data: {logs: [...], pagination: {...}}}
  if (data.data?.logs) {
    return {
      logs: data.data.logs,
      pagination: data.data.pagination || {
        page: filters.page || 1,
        limit: filters.limit || 20,
        total: data.data.logs.length,
        pages: 1,
      },
    };
  }

  // Handle direct array response
  if (Array.isArray(data)) {
    return {
      logs: data,
      pagination: {
        page: filters.page || 1,
        limit: filters.limit || 20,
        total: data.length,
        pages: 1,
      },
    };
  }

  return data;
};

// Fetch a single audit log detail
const fetchAuditLogDetail = async (id: string): Promise<AuditLog> => {
  const response = await fetch(`${API_BASE_URL}/audit-logs/${id}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch audit log detail');
  }

  const data = await response.json();
  return data.data || data;
};

// Fetch users for filter dropdown
const fetchUsers = async (): Promise<User[]> => {
  const response = await fetch(`${API_BASE_URL}/users`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  const result = await response.json();
  // API returns {success: true, data: [...]} - extract the data array
  return result.data || result;
};

// Export audit logs
const exportAuditLogs = async (
  filters: AuditLogFilters,
  format: 'csv' | 'json'
): Promise<Blob> => {
  const params = new URLSearchParams();

  if (filters.userId) params.append('userId', filters.userId);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  if (filters.types && filters.types.length > 0) {
    params.append('types', filters.types.join(','));
  }
  params.append('format', format);

  const response = await fetch(`${API_BASE_URL}/audit-logs/export?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to export audit logs');
  }

  return response.blob();
};

export interface UseAuditLogsReturn {
  logs: AuditLog[];
  pagination: AuditLogPagination;
  isLoading: boolean;
  error: Error | null;
  filters: AuditLogFilters;
  setFilters: (filters: AuditLogFilters) => void;
  updateFilter: (key: keyof AuditLogFilters, value: any) => void;
  clearFilters: () => void;
  refetch: () => void;
  setPage: (page: number) => void;
}

const DEFAULT_FILTERS: AuditLogFilters = {
  page: 1,
  limit: 20,
};

export const useAuditLogs = (initialFilters?: Partial<AuditLogFilters>): UseAuditLogsReturn => {
  const [filters, setFiltersState] = useState<AuditLogFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => fetchAuditLogs(filters),
  });

  const setFilters = useCallback((newFilters: AuditLogFilters) => {
    setFiltersState({ ...newFilters, page: 1 }); // Reset to page 1 on filter change
  }, []);

  const updateFilter = useCallback((key: keyof AuditLogFilters, value: any) => {
    setFiltersState(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1, // Reset to page 1 unless changing page
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const setPage = useCallback((page: number) => {
    setFiltersState(prev => ({ ...prev, page }));
  }, []);

  return {
    logs: data?.logs || [],
    pagination: data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
    isLoading,
    error: error as Error | null,
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    refetch,
    setPage,
  };
};

// Hook for fetching a single audit log detail
export const useAuditLogDetail = (id: string | null) => {
  return useQuery({
    queryKey: ['auditLogDetail', id],
    queryFn: () => fetchAuditLogDetail(id!),
    enabled: !!id,
  });
};

// Hook for fetching users (for filter dropdown)
export const useAuditLogUsers = () => {
  return useQuery({
    queryKey: ['auditLogUsers'],
    queryFn: fetchUsers,
  });
};

// Hook for exporting audit logs
export const useExportAuditLogs = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<Error | null>(null);

  const exportLogs = useCallback(async (
    filters: AuditLogFilters,
    format: 'csv' | 'json'
  ) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const blob = await exportAuditLogs(filters, format);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err as Error);
      throw err;
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    exportLogs,
    isExporting,
    exportError,
  };
};
